/**
 * Wrappers for the agent-facing `/api/v1/tickets/{ticket_id}/ai/*` endpoints
 * (Tiqora AI subsystem, Phase B/C — see `~/TIQORA_LLM_PLAN.md` §3.4/§3.5).
 *
 * Hand-written for the same reason as `./aiApi.ts` (the admin-facing
 * counterpart): this page set is built in a worktree scoped to
 * `frontend/src/` only, and `packages/api-client/src/client.ts` does not yet
 * expose wrapper methods for these routes even though the response shapes
 * are already generated in `schema.d.ts` (`AiStateOut`, `AiDraftOut`,
 * `AiDraftRequestOut`, `AiSummarizeOut`). The types below mirror
 * `backend/src/tiqora/api/v1/ai.py` exactly; once the shared client picks up
 * bindings for these routes, callers can switch to `api.getTicketAi` etc.
 * without changing call sites here.
 */
import { api } from "./api";

export type AiDraftKind = "reply" | "clarify";
export type AiDraftStatus = "open" | "accepted" | "discarded";
export type AiDraftSource = "auto" | "manual";

export type AiToolTraceOut = {
  name: string;
  content: string;
  /** JSON the tool was called with; null on traces recorded before it was kept. */
  arguments?: string | null;
};

export type AiDraftOut = {
  id: number;
  ticket_id: number;
  kind: string;
  subject: string | null;
  body: string;
  based_on_article_id: number | null;
  status: string;
  source: string;
  accepted_article_id: number | null;
  create_time: string;
  tool_trace: AiToolTraceOut[];
};

/** Manual Assist background-run status (nginx-90s-timeout fix — the draft
 * POST returns immediately and the frontend polls this instead of the
 * response). ``null`` when no manual run has ever been started for this
 * ticket. */
export type ManualRunStatus =
  | "running"
  | "drafted"
  | "skipped"
  | "escalated"
  // The agent ended the run on purpose without customer text — advertising, a
  // newsletter, nothing to act on (backend: STATUS_NO_REPLY / the
  // no_reply_needed tool).
  | "no_reply"
  | "superseded"
  | "error";

export type AiStateOut = {
  manual_assist_available: boolean;
  summary_available: boolean;
  can_summarize: boolean;
  operation_mode_ready: boolean;
  drafts: AiDraftOut[];
  summary_body: string | null;
  last_summary_upto_article_id: number | null;
  summary_created_at: string | null;
  manual_run_status?: ManualRunStatus | null;
  manual_run_notes?: string | null;
  manual_run_error_code?: string | null;
  manual_run_started_at?: string | null;
  ai_escalated_at?: string | null;
  /** Set while automatic AI actions are paused for this ticket. */
  ai_paused_at?: string | null;
  ai_paused_by_name?: string | null;
  /** Why the auto worker last skipped a customer article on this ticket
   * (`budget_tokens_day`, `queue_rate_limit`, `llm_unavailable`, …). */
  auto_skip_reason?: string | null;
  auto_skip_at?: string | null;
  /** Pending triage proposal, only present while its status is "open". */
  triage?: AiTriageOut | null;
  /** The per-ticket autopilot switch in the ticket header. */
  autopilot?: AiAutopilotOut | null;
};

/**
 * `unavailable`: auto-reply is off in the queue. `stopped`: an agent stopped
 * it. `handed_over`: the AI gave the ticket to the team (`reason` says why).
 * `active`: answering, with `grant_*` set while a release of N replies runs.
 */
export type AutopilotMode = "active" | "stopped" | "handed_over" | "unavailable";

export type AiAutopilotOut = {
  mode: AutopilotMode;
  grant_remaining?: number | null;
  grant_total?: number | null;
  by_name?: string | null;
  since?: string | null;
  /** `max_clarifications`, `max_auto_replies`, `grant_used`,
   * `escalate_to_human`, `identity` — only for `handed_over`. */
  reason?: string | null;
  /** The newest customer message has no answer yet. */
  unanswered_customer_message?: boolean;
  /** The ticket's customer user / company, for "stop for this customer". */
  customer_user_id?: string | null;
  customer_id?: string | null;
  /** With `reason: "customer_paused"`: which pause applies, and until when
   * (`null` = until lifted). */
  customer_pause_scope?: "user" | "company" | null;
  customer_paused_until?: string | null;
};

export type AiCustomerPauseIn = {
  scope: "user" | "company";
  /** `null` = until lifted. */
  days: number | null;
};

export type AiAutopilotIn =
  | { action: "stop" }
  | { action: "start"; runs: number; answer_latest?: boolean };

/** Largest release one start may grant (backend AUTOPILOT_MAX_RUNS). */
export const AUTOPILOT_MAX_RUNS = 20;

/**
 * A pending triage proposal. The queue half and the customer half are
 * independent -- either can be absent, and an agent accepts them separately.
 */
export type AiTriageOut = {
  id: number;
  status: string;
  source_queue_id: number;
  suggested_queue_id?: number | null;
  suggested_queue_name?: string | null;
  queue_confidence?: number | null;
  queue_reason?: string | null;
  /** How many of the samples voted for the winning queue. */
  queue_votes?: number | null;
  extracted_email?: string | null;
  suggested_customer_user_id?: string | null;
  suggested_customer_name?: string | null;
  customer_confidence?: number | null;
  created_at?: string | null;
};

export type SummaryDetail = "standard" | "detailed";

export type AiSummarizeOut = {
  status: string;
  summary_body?: string | null;
  upto_article_id?: number | null;
};

/** Summary along the agent's own instruction — returned once, never stored. */
export type AiCustomSummaryOut = {
  summary_body: string;
  created_at: string;
};

export type AiDraftRequestOut = {
  status: string;
  draft_id?: number | null;
  article_id?: number | null;
  notes?: string | null;
};

export const ticketAiApi = {
  getState(ticketId: number, signal?: AbortSignal) {
    return api.request<AiStateOut>("GET", `/api/v1/tickets/${ticketId}/ai`, {
      signal,
    });
  },
  requestDraft(ticketId: number, signal?: AbortSignal) {
    return api.request<AiDraftRequestOut>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/draft`,
      {
        signal,
      },
    );
  },
  summarize(ticketId: number, detail?: SummaryDetail, signal?: AbortSignal) {
    return api.request<AiSummarizeOut>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/summarize`,
      {
        body: detail ? { detail } : undefined,
        signal,
      },
    );
  },
  customSummary(ticketId: number, instruction: string, signal?: AbortSignal) {
    return api.request<AiCustomSummaryOut>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/summarize/custom`,
      { body: { instruction }, signal },
    );
  },
  discardDraft(ticketId: number, draftId: number, signal?: AbortSignal) {
    return api.request<void>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/drafts/${draftId}/discard`,
      { signal },
    );
  },
  resume(ticketId: number, signal?: AbortSignal) {
    return api.request<void>("POST", `/api/v1/tickets/${ticketId}/ai/resume`, {
      signal,
    });
  },
  autopilot(ticketId: number, body: AiAutopilotIn, signal?: AbortSignal) {
    return api.request<AiAutopilotOut>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/autopilot`,
      { body, signal },
    );
  },
  /** Stop the automation for the ticket's customer (all their tickets). */
  customerPause(ticketId: number, body: AiCustomerPauseIn, signal?: AbortSignal) {
    return api.request<AiAutopilotOut>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/customer-pause`,
      { body, signal },
    );
  },
  liftCustomerPause(ticketId: number, signal?: AbortSignal) {
    return api.request<AiAutopilotOut>(
      "DELETE",
      `/api/v1/tickets/${ticketId}/ai/customer-pause`,
      { signal },
    );
  },
  pause(ticketId: number, signal?: AbortSignal) {
    return api.request<void>("POST", `/api/v1/tickets/${ticketId}/ai/pause`, {
      signal,
    });
  },
  unpause(ticketId: number, signal?: AbortSignal) {
    return api.request<void>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/unpause`,
      { signal },
    );
  },
  /**
   * Apply a triage proposal. Runs with the agent's own permissions, so this
   * can 403 on a target queue the worker itself would have been allowed to
   * move into (see the backend route docstring).
   */
  acceptTriage(
    ticketId: number,
    triageId: number,
    parts: { queue: boolean; customer: boolean },
    signal?: AbortSignal,
  ) {
    return api.request<void>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/triage/${triageId}/accept`,
      { body: parts, signal },
    );
  },
  rejectTriage(
    ticketId: number,
    triageId: number,
    note?: string,
    signal?: AbortSignal,
  ) {
    return api.request<void>(
      "POST",
      `/api/v1/tickets/${ticketId}/ai/triage/${triageId}/reject`,
      { body: { note: note ?? null }, signal },
    );
  },
};
